import { t } from "@travily/i18n";
import { Frame, type FrameContentMaxWidth } from "@ui/components/Frame";
import { Icon } from "@ui/components/Icon";
import { Logo } from "@ui/components/Logo";
import { OutlineButton } from "@ui/components/OutlineButton";
import { StandardButton } from "@ui/components/StandardButton";
import { TopBarProgress } from "@ui/components/TopBar/TopBarProgress";
import { tv } from "@ui/lib/tv";
import { Button } from "@ui/primitives/Button";
import { cx } from "@ui/utils/cx";
import { type ReactElement, type ReactNode, useMemo } from "react";
import { WizardEntitiesStage } from "./components/WizardEntitiesStage";
import { WizardFieldError } from "./components/WizardFieldError";
import { WizardPrimaryButton, type WizardPrimaryButtonProps } from "./components/WizardPrimaryButton";
import { WizardSecondaryButton, type WizardSecondaryButtonProps } from "./components/WizardSecondaryButton";
import { WizardStage } from "./components/WizardStage";
import { WizardTertiaryButton, type WizardTertiaryButtonProps } from "./components/WizardTertiaryButton";
import { useWizardSlots } from "./use-wizard-slots";
import {
  type WizardActionButtonProps,
  type WizardColor,
  WizardContext,
  type WizardContextValue,
} from "./WizardContext";

const slots = {
  Stage: WizardStage,
  EntitiesStage: WizardEntitiesStage,
  FieldError: WizardFieldError,
  PrimaryButton: WizardPrimaryButton,
  SecondaryButton: WizardSecondaryButton,
  TertiaryButton: WizardTertiaryButton,
} as const;

const wizard = tv({
  slots: {
    topBar: "flex flex-col bg-white",
    topBarRow: "flex w-full items-center justify-between gap-2 px-5 md:px-8",
    topBarRowMobile: "h-16 md:hidden",
    topBarRowDesktop: "hidden h-[88px] md:flex",
    topBarStart: "flex shrink-0 items-center",
    topBarEnd: "flex shrink-0 items-center gap-2",
    progress: "px-5 pb-2 md:px-8 md:pb-3",
    /** Reserves space at the bottom of the content area so the fixed bottom bar
     *  never overlaps the last item the user wants to reach. */
    contentSpacer: "pb-24 md:pb-28",
    bottomBar:
      "fixed inset-x-0 bottom-0 z-30 flex flex-col gap-2 border-t border-neutral-100 bg-white px-5 py-3 pb-safe-[0.75rem] md:flex-row md:items-center md:px-8 md:py-4",
    bottomBarBack: "hidden md:inline-flex",
    bottomBarSpacer: "hidden md:block md:flex-1",
    bottomBarEnd: "flex flex-col gap-2 md:flex-row md:items-center md:gap-3",
  },
});

export interface WizardProps {
  /** Wizard color theme — drives progress bar and primary button. */
  color: WizardColor;
  /** Current stage, 1-indexed. */
  currentStage: number;
  /** Total stages. When `<= 1` the progress bar is hidden — useful for entry pages. */
  totalStages: number;
  /** Cap on the stage content width. Default `medium` fits single-control stages;
   *  entry pages with wide layouts (e.g. a card grid) can pass `xlarge`. */
  contentMaxWidth?: FrameContentMaxWidth;
  /** Back handler. When omitted, no back affordance is rendered (use on the first stage). */
  onBack?: () => void;
  /** Logo destination on desktop and when no back affordance is rendered on mobile. */
  homeHref?: string;
  /** Logo `aria-label` for assistive tech. Defaults to the Travily logo label key. */
  logoLabel?: string;
  children: ReactNode;
  className?: string;
}

function Root({
  color,
  currentStage,
  totalStages,
  contentMaxWidth = "medium",
  onBack,
  homeHref = "/",
  logoLabel,
  children,
  className,
}: WizardProps) {
  const styles = wizard();
  const childSlots = useWizardSlots(children, slots);

  const primaryProps = childSlots.primaryButton ? propsOf<WizardPrimaryButtonProps>(childSlots.primaryButton) : null;
  const secondaryConfigs = childSlots.secondaryButtons.map(propsOf<WizardSecondaryButtonProps>);
  const tertiaryConfigs = childSlots.tertiaryButtons.map(propsOf<WizardTertiaryButtonProps>);

  const topBarSecondaries = secondaryConfigs.filter((cfg) => cfg.placement === "topBarOnMobile");
  const bottomBarSecondaries = secondaryConfigs.filter((cfg) => cfg.placement !== "topBarOnMobile");

  const contextValue = useMemo<WizardContextValue>(
    () => ({ color, currentStage, totalStages }),
    [color, currentStage, totalStages],
  );

  const stageContent = childSlots.stage ?? childSlots.entitiesStage;
  const backLabel = t("common.actions.back");
  const resolvedLogoLabel = logoLabel ?? t("components.top_bar.logo");
  const showProgress = totalStages > 1;
  const hasBottomBar = primaryProps !== null || bottomBarSecondaries.length > 0 || onBack !== undefined;

  return (
    <WizardContext value={contextValue}>
      <Frame className={className}>
        <Frame.TopBar>
          <header className={styles.topBar()}>
            <div className={cx(styles.topBarRow(), styles.topBarRowMobile())}>
              <div className={styles.topBarStart()}>
                {onBack ? (
                  <BackButton variant="icon" label={backLabel} onPress={onBack} />
                ) : (
                  <Logo variant="mark" color={color} href={homeHref} label={resolvedLogoLabel} className="h-9" />
                )}
              </div>
              <div className={styles.topBarEnd()}>
                {tertiaryConfigs.map((cfg, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: configs are static for the page lifetime
                  <TertiaryButtonRender key={`t-m-${i}`} cfg={cfg} viewport="mobile" />
                ))}
                {topBarSecondaries.map((cfg, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: configs are static for the page lifetime
                  <SecondaryButtonRender key={`s-m-${i}`} cfg={cfg} viewport="mobile" color={color} />
                ))}
              </div>
            </div>

            <div className={cx(styles.topBarRow(), styles.topBarRowDesktop())}>
              <div className={styles.topBarStart()}>
                <Logo variant="full" color={color} href={homeHref} label={resolvedLogoLabel} className="h-[42px]" />
              </div>
              <div className={styles.topBarEnd()}>
                {tertiaryConfigs.map((cfg, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: configs are static for the page lifetime
                  <TertiaryButtonRender key={`t-d-${i}`} cfg={cfg} viewport="desktop" />
                ))}
              </div>
            </div>

            {showProgress ? (
              <div className={styles.progress()}>
                <TopBarProgress steps={totalStages} currentStep={currentStage} color={color} />
              </div>
            ) : null}
          </header>
        </Frame.TopBar>

        <Frame.Content maxWidth={contentMaxWidth}>
          <div className={cx("flex flex-1 flex-col", hasBottomBar && styles.contentSpacer())}>{stageContent}</div>
        </Frame.Content>

        {hasBottomBar ? (
          <Frame.BottomBar>
            <footer className={styles.bottomBar()}>
              {onBack ? (
                <div className={styles.bottomBarBack()}>
                  <BackButton variant="text" label={backLabel} onPress={onBack} />
                </div>
              ) : null}
              <div className={styles.bottomBarSpacer()} />
              <div className={styles.bottomBarEnd()}>
                {bottomBarSecondaries.map((cfg, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: configs are static for the page lifetime
                  <SecondaryButtonRender key={`s-b-${i}`} cfg={cfg} viewport="bottomBar" color={color} />
                ))}
                {primaryProps ? <PrimaryButtonRender cfg={primaryProps} color={color} /> : null}
              </div>
            </footer>
          </Frame.BottomBar>
        ) : null}
      </Frame>
    </WizardContext>
  );
}

function propsOf<T>(el: ReactElement): T {
  return el.props as T;
}

interface PrimaryRenderProps {
  cfg: WizardPrimaryButtonProps;
  color: WizardColor;
}

function PrimaryButtonRender({ cfg, color }: PrimaryRenderProps) {
  return (
    <>
      <ButtonInstance
        cfg={cfg}
        viewport="mobile"
        hierarchy="primary"
        color={color}
        size="56"
        className="inline-flex w-full justify-center md:hidden"
      />
      <ButtonInstance
        cfg={cfg}
        viewport="desktop"
        hierarchy="primary"
        color={color}
        size="56"
        className="hidden md:inline-flex lg:hidden"
      />
      <ButtonInstance
        cfg={cfg}
        viewport="desktop"
        hierarchy="primary"
        color={color}
        size="64"
        className="hidden lg:inline-flex"
      />
    </>
  );
}

interface SecondaryRenderProps {
  cfg: WizardSecondaryButtonProps;
  viewport: "mobile" | "bottomBar";
  color: WizardColor;
}

function SecondaryButtonRender({ cfg, viewport, color }: SecondaryRenderProps) {
  if (viewport === "mobile") {
    return (
      <ButtonInstance
        cfg={cfg}
        viewport="mobile"
        hierarchy="secondary"
        color={color}
        size="44"
        className="inline-flex md:hidden"
      />
    );
  }

  return (
    <>
      <ButtonInstance
        cfg={cfg}
        viewport="mobile"
        hierarchy="secondary"
        color={color}
        size="56"
        className="inline-flex w-full justify-center md:hidden"
      />
      <ButtonInstance
        cfg={cfg}
        viewport="desktop"
        hierarchy="secondary"
        color={color}
        size="56"
        className="hidden md:inline-flex lg:hidden"
      />
      <ButtonInstance
        cfg={cfg}
        viewport="desktop"
        hierarchy="secondary"
        color={color}
        size="64"
        className="hidden lg:inline-flex"
      />
    </>
  );
}

interface TertiaryRenderProps {
  cfg: WizardTertiaryButtonProps;
  viewport: "mobile" | "desktop";
}

function resolveContent(cfg: WizardActionButtonProps, isMobile: boolean) {
  const iconOnly = isMobile && cfg.mobileIcon !== undefined;
  const ariaLabel = iconOnly && typeof cfg.mobileLabel === "string" ? cfg.mobileLabel : undefined;
  const leadIcon = iconOnly ? cfg.mobileIcon : cfg.leadIcon;
  const trailIcon = iconOnly ? undefined : cfg.trailIcon;
  const content = iconOnly ? null : isMobile && cfg.mobileLabel !== undefined ? cfg.mobileLabel : cfg.children;
  return { iconOnly, ariaLabel, leadIcon, trailIcon, content };
}

function TertiaryButtonRender({ cfg, viewport }: TertiaryRenderProps) {
  const isMobile = viewport === "mobile";
  const { iconOnly, ariaLabel, leadIcon, trailIcon, content } = resolveContent(cfg, isMobile);
  const visibilityClass = isMobile ? "inline-flex md:hidden" : "hidden md:inline-flex";

  const sharedProps = {
    "aria-label": ariaLabel,
    className: visibilityClass,
    isDisabled: cfg.isDisabled,
    leadIcon,
    shape: iconOnly ? ("circle" as const) : ("pill" as const),
    size: "40" as const,
    trailIcon,
  };

  return cfg.href !== undefined ? (
    <OutlineButton {...sharedProps} href={cfg.href}>
      {content}
    </OutlineButton>
  ) : (
    <OutlineButton {...sharedProps} onPress={cfg.onPress}>
      {content}
    </OutlineButton>
  );
}

interface ButtonInstanceProps {
  cfg: WizardPrimaryButtonProps | WizardSecondaryButtonProps;
  viewport: "mobile" | "desktop";
  hierarchy: "primary" | "secondary";
  color: WizardColor;
  size: "44" | "56" | "60" | "64";
  className?: string;
}

function ButtonInstance({ cfg, viewport, hierarchy, color, size, className }: ButtonInstanceProps) {
  const { ariaLabel, leadIcon, trailIcon, content } = resolveContent(cfg, viewport === "mobile");

  const sharedProps = {
    "aria-label": ariaLabel,
    className,
    color,
    hierarchy,
    isDisabled: cfg.isDisabled,
    leadIcon,
    size,
    trailIcon,
  };

  if (cfg.href !== undefined) {
    return (
      <StandardButton {...sharedProps} href={cfg.href}>
        {content}
      </StandardButton>
    );
  }

  const isPending = "isPending" in cfg ? cfg.isPending : undefined;
  return (
    <StandardButton {...sharedProps} isPending={isPending} onPress={cfg.onPress}>
      {content}
    </StandardButton>
  );
}

interface BackButtonProps {
  variant: "icon" | "text";
  label: string;
  onPress: () => void;
}

function BackButton({ variant, label, onPress }: BackButtonProps) {
  if (variant === "icon") {
    return (
      <Button
        aria-label={label}
        className="-ml-2 inline-flex cursor-pointer items-center justify-center rounded-full p-2 text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900"
        onPress={onPress}
      >
        <Icon name="arrowLeft" size="20" />
      </Button>
    );
  }

  const leadIcon = <Icon name="arrowLeft" size="16" />;
  return (
    <>
      <StandardButton
        className="hidden md:inline-flex lg:hidden"
        color="neutral"
        hierarchy="link"
        leadIcon={leadIcon}
        onPress={onPress}
        size="56"
      >
        {label}
      </StandardButton>
      <StandardButton
        className="hidden lg:inline-flex"
        color="neutral"
        hierarchy="link"
        leadIcon={leadIcon}
        onPress={onPress}
        size="64"
      >
        {label}
      </StandardButton>
    </>
  );
}

export const Wizard = Object.assign(Root, slots);
